/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME } from '../../../config';
import { DeviceFeatures } from '../../../core/device';
import { QoEAppLogger } from '../../../core/logging';
import { CursorManager } from '../../../core/navigation';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { PlaybackRestrictionType } from '../../../core/videoplayer/enums';
import { AiringPreviewUtils } from '../../../core/videoplayer/utils';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { prepareVodResumeStartOverOverlayFromLiveTv } from '../../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import { hideVideoDebugOverlay } from '../../videoplayer/videodebugoverlay/videodebugoverlay.reducers';
import {
    videoPlayerIsMediaButtonPressedAction,
    videoPlayerSetPlaybackStateAction,
    videoPlayerToggleIsPlayingAction,
} from '../../videoplayer/videoplayer.actions';
import {
    handleAdvance,
    handleFastForward,
    handleReplay,
    handleRewind,
    hideNotSupportedText,
    resetPlaybackParamsVideoplayer,
    showPCPinOverlayForVideoPlayer,
    toggleIsPlaying,
} from '../../videoplayer/videoplayer.reducers';
import { showOneLineGuide } from '../onelineguide/onelineguide.reducers';
import {
    setCurrentActiveIndexAction,
    updateTrickPlayBarLongKeyPressIntervalIdAction,
} from './infobanner.actions';
import {
    clearLongKeyPressInterval,
    hideInfoBanners,
    hideTrickPlayMenu,
    showFullInfo,
    showTrickPlayMenu,
    updateStandardInfoBannerTimer,
    updateTrickPlayMenuTimer,
} from './infobanner.reducers';
import { MediaButtonTypes } from './mediabuttontypes';
import { PlaybackState, TrickPlayType } from './PlaybackTypes';

export const onKeyLeftTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    dispatch(updateStandardInfoBannerTimer());
    dispatch(updateTrickPlayMenuTimer());
    const { infoBanner } = getState();
    const { currentActiveIndex, activePositionsAvailable } = infoBanner;
    const positionsSize = activePositionsAvailable.size;
    if (positionsSize && currentActiveIndex > 0) {
        let currentIndex = currentActiveIndex;
        let index = currentActiveIndex - 1;
        if (!activePositionsAvailable.get(index).isActive) {
            for (index; index >= 0; index -= 1) {
                if (activePositionsAvailable.get(index).isActive) {
                    currentIndex = index;
                    break;
                }
            }
        } else {
            currentIndex = index;
        }
        dispatch(setCurrentActiveIndexAction(currentIndex));
    }

    dispatch(hideNotSupportedText());
};

export const onKeyLongLeftTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { trickPlayBarLongKeyPressIntervalId } = getState().infoBanner;
    if (!trickPlayBarLongKeyPressIntervalId) {
        const intervalId = setInterval(() => {
            dispatch(onKeyLeftTrickplayMenu());
        }, TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME);
        dispatch(updateTrickPlayBarLongKeyPressIntervalIdAction(intervalId));
    }
};

export const clearLongPressIntervalTrickplayMenu = () => (dispatch: Function) => {
    dispatch(clearLongKeyPressInterval());
};

export const onKeyRightTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    dispatch(updateStandardInfoBannerTimer());
    dispatch(updateTrickPlayMenuTimer());
    const { infoBanner } = getState();
    const { currentActiveIndex, activePositionsAvailable } = infoBanner;
    const positionsSize = activePositionsAvailable.size;
    if (positionsSize && currentActiveIndex < positionsSize - 1) {
        let index = currentActiveIndex + 1;
        if (!activePositionsAvailable.get(index).isActive) {
            for (index; index < activePositionsAvailable.size - 1; index += 1) {
                if (activePositionsAvailable.get(index).isActive) {
                    break;
                }
            }
        }
        dispatch(setCurrentActiveIndexAction(index));
    }

    dispatch(hideNotSupportedText());
};

export const onKeyLongRightTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { trickPlayBarLongKeyPressIntervalId } = getState().infoBanner;
    if (!trickPlayBarLongKeyPressIntervalId) {
        const intervalId = setInterval(() => {
            dispatch(onKeyRightTrickplayMenu());
        }, TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME);
        dispatch(updateTrickPlayBarLongKeyPressIntervalIdAction(intervalId));
    }
};

export const onKeyOkTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { infoBanner, videoPlayer } = getState();
    const { currentActiveIndex, activePositionsAvailable } = infoBanner;
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    const oTrickplayControl = oVideoPlaybackController.getTrickplayControl();
    const { airingOffers, offerPreview } = oVideoPlaybackController.getMetadata();
    const { playbackState } = videoPlayer;
    const isCursorOn = CursorManager.isCursorAppeared();
    let activePosition = activePositionsAvailable.get(currentActiveIndex)?.type;
    const dataFormatter = new AiringPreviewUtils(airingOffers, offerPreview);
    const isAvailableInSocu = dataFormatter.getIsAvailableInSocu();
    const isSocuEnabled = DeviceFeatures.isSocuEnabled();

    if (isCursorOn) {
        const { value } = getState().inputFactory;
        const valueInt = Number(value);
        if (valueInt >= 0) {
            activePosition = valueInt;
            dispatch(setCurrentActiveIndexAction(activePosition));
        }
    }
    dispatch(updateTrickPlayMenuTimer());
    dispatch(updateStandardInfoBannerTimer());

    const isOLGButton = activePosition === MediaButtonTypes.OLG_BUTTON_INDEX;
    const isInfoButton = activePosition === MediaButtonTypes.INFO_BUTTON_INDEX;
    const adultRestriction =
        VideoPlaybackController.getInstance().getRestrictionType() ===
        PlaybackRestrictionType.ADULT;
    if (
        adultRestriction &&
        !isOLGButton &&
        VideoPlaybackController.getInstance().isNetworkConnected()
    ) {
        dispatch(hideInfoBanners());

        const fnCallback = isInfoButton ? showFullInfo : undefined;
        dispatch(showPCPinOverlayForVideoPlayer(fnCallback));
        return;
    }

    switch (activePosition) {
        case MediaButtonTypes.START_FROM_BUFFER_BUTTON_INDEX:
            // socu is available in preview data but socu content is not playing.
            if (
                isSocuEnabled &&
                isAvailableInSocu &&
                !VideoPlaybackController.getInstance().isSocu()
            ) {
                if (oVideoPlaybackController.isTrickplayEnabled()) {
                    oTrickplayControl.stopScrubbing();
                    dispatch(videoPlayerToggleIsPlayingAction(false));
                    dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PAUSE));
                    oVideoPlaybackController.pause(true);
                }
                dispatch(hideInfoBanners());
                dispatch(prepareVodResumeStartOverOverlayFromLiveTv());
            } else {
                QoEAppLogger.setIsUserTriggerAction(true);
                oTrickplayControl.startFromBuffer();
                if (
                    !oVideoPlaybackController.isTrickplayEnabled() &&
                    playbackState !== PlaybackState.PLAY
                )
                    dispatch(toggleIsPlaying());
            }
            break;
        case MediaButtonTypes.REPLAY_BUTTON_INDEX:
            dispatch(handleReplay());
            oVideoPlaybackController.updateLastViewedPosition();
            break;
        case MediaButtonTypes.REWIND_BUTTON_INDEX:
            dispatch(handleRewind());
            break;
        case MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX:
            if (
                oVideoPlaybackController.isPlaybackStarted() &&
                oVideoPlaybackController.isNetworkConnected()
            ) {
                if (oVideoPlaybackController.trickModeRestrictions()?.allowPause) {
                    dispatch(toggleIsPlaying());
                } else if (oVideoPlaybackController.isTrickplayEnabled()) {
                    dispatch(toggleIsPlaying());
                } else {
                    dispatch(
                        videoPlayerIsMediaButtonPressedAction({
                            type: TrickPlayType.PAUSE,
                            isMediaButtonPressed: true,
                        }),
                    );
                }
            }
            break;
        case MediaButtonTypes.FORWARD_BUTTON_INDEX:
            dispatch(handleFastForward());
            break;
        case MediaButtonTypes.ADVANCE_BUTTON_INDEX:
            dispatch(handleAdvance());
            oVideoPlaybackController.updateLastViewedPosition();
            break;
        case MediaButtonTypes.GO_TO_LIVETV_BUTTON_INDEX:
            // TODO: Need to check if socu content is playing then handle Go to End
            QoEAppLogger.setIsUserTriggerAction(true);
            oTrickplayControl.goToLive();
            if (
                !oVideoPlaybackController.isTrickplayEnabled() &&
                playbackState !== PlaybackState.PLAY
            )
                dispatch(toggleIsPlaying());
            break;
        case MediaButtonTypes.LIVE_BUTTON_INDEX:
            dispatch(resetPlaybackParamsVideoplayer());
            dispatch(hideInfoBanners());
            oVideoPlaybackController.switchToLiveTv();
            break;
        case MediaButtonTypes.INFO_BUTTON_INDEX:
            if (oVideoPlaybackController.isTrickplayEnabled()) {
                dispatch(toggleIsPlaying());
            }
            dispatch(hideTrickPlayMenu());
            dispatch(showFullInfo());
            break;
        case MediaButtonTypes.OLG_BUTTON_INDEX:
            dispatch(hideTrickPlayMenu());
            dispatch(showOneLineGuide());
            break;
        default:
        // callback to default case
    }
};

export const onKeyBackTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { isVideoDebugOverlayActive } = getState().videoDebugOverlay;
    if (isVideoDebugOverlayActive) {
        dispatch(hideVideoDebugOverlay());
    }
    if (
        VideoPlaybackController.getInstance().isTrickplayEnabled() &&
        VideoPlaybackController.getInstance().isNetworkConnected()
    )
        dispatch(toggleIsPlaying());
    dispatch(hideTrickPlayMenu());
};

export const onKeyInfoTrickplayMenu = () => (dispatch: Function) => {
    if (VideoPlaybackController.getInstance().isTrickplayEnabled()) dispatch(toggleIsPlaying());
    dispatch(hideTrickPlayMenu());
    dispatch(showFullInfo());
};

export const onKeyChannelDownTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { isTrickplayMenuActive } = infoBanner;
    if (!isTrickplayMenuActive) dispatch(showTrickPlayMenu());
    else {
        dispatch(updateTrickPlayMenuTimer());
        dispatch(updateStandardInfoBannerTimer());
    }
};

export const onKeyChannelUpTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { isTrickplayMenuActive } = infoBanner;
    if (!isTrickplayMenuActive) dispatch(showTrickPlayMenu());
    else {
        dispatch(updateTrickPlayMenuTimer());
        dispatch(updateStandardInfoBannerTimer());
    }
};
